import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { fade, makeStyles, useTheme } from "@material-ui/core/styles";
import AddCommentIcon from "@material-ui/icons/AddComment";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import OsInteractionDialog from "../../components/OsInteractionDialog";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import useOsCatalogs from "../../hooks/useOsCatalogs";
import api from "../../services/api";

const TWO_MINUTES = 2 * 60 * 1000;

const useStyles = makeStyles(theme => ({
  filters: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5)
  },
  filterField: {
    minWidth: 210
  },
  searchField: {
    minWidth: 240,
    flex: "1 1 260px"
  },
  scope: {
    padding: theme.spacing(0, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  boardPaper: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex"
  },
  board: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    overflowX: "auto",
    overflowY: "hidden",
    backgroundColor: theme.palette.background.default,
    ...theme.scrollbarStyles
  },
  column: {
    flex: "1 0 290px",
    minWidth: 280,
    maxWidth: 360,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: theme.shape.borderRadius
  },
  columnHeader: {
    color: "#fff",
    padding: theme.spacing(1.25, 1.5),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  columnCount: {
    color: "inherit",
    borderColor: fade("#fff", 0.65),
    backgroundColor: fade("#fff", 0.12),
    fontWeight: 600
  },
  cards: {
    flex: 1,
    minHeight: 120,
    padding: theme.spacing(1),
    overflowY: "auto",
    backgroundColor: fade(theme.palette.background.default, 0.65),
    ...theme.scrollbarStyles
  },
  card: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    cursor: "grab",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    transition: "box-shadow 120ms ease, transform 120ms ease",
    "&:hover": {
      boxShadow: theme.shadows[3],
      transform: "translateY(-1px)"
    },
    "&:active": {
      cursor: "grabbing"
    }
  },
  dragging: {
    opacity: 0.45
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75)
  },
  cardId: {
    color: theme.palette.primary.main,
    fontWeight: 700
  },
  priority: {
    marginLeft: "auto",
    maxWidth: 120
  },
  client: {
    fontWeight: 600,
    marginTop: theme.spacing(0.75),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  description: {
    marginTop: theme.spacing(0.75),
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: "2.5em"
  },
  metadata: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  cardFooter: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center"
  },
  updatedAt: {
    color: theme.palette.text.secondary,
    flex: 1
  },
  empty: {
    height: "100%",
    minHeight: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    textAlign: "center",
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  loading: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  menuIcon: {
    minWidth: 34
  }
}));

const statusPalette = [
  "#607d8b",
  "#00897b",
  "#f9a825",
  "#7b1fa2",
  "#1976d2",
  "#ec407a",
  "#3949ab"
];

const isFinalStatus = status => [3, 4].includes(Number(status?.ind_tipo));

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

const updatePayload = (order, statusId) => {
  const payload = {
    grupo_id: order.grupo_id,
    cliente_id: order.cliente_id,
    objeto_id: order.objeto_id,
    objeto_padrao_id: order.objeto_padrao_id,
    tecnico_id: order.tecnico_id,
    status_id: statusId,
    prioridade_id: order.prioridade_id,
    tipo_id: order.tipo_id,
    canal_id: order.canal_id,
    descricao_curta: order.descricao_curta,
    descricao: order.descricao,
    data_previsao_inicio: order.data_previsao_inicio,
    data_previsao_termino: order.data_previsao_termino,
    cobrar: Boolean(order.cobrar),
    valor_total: Number(order.valor_total || 0),
    tempo_estimado: Number(order.tempo_estimado || 0)
  };

  if (order.contato_id) {
    payload.contato_id = order.contato_id;
  }

  return payload;
};

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);
  const {
    data: catalogs,
    isLoading: catalogsLoading,
    error: catalogsError
  } = useOsCatalogs();

  const [groupId, setGroupId] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [periodDays, setPeriodDays] = useState(30);
  const [scope, setScope] = useState(user?.IDTecnicoOS ? "mine" : "all");
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [interactionOrder, setInteractionOrder] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOrder, setMenuOrder] = useState(null);
  const boardRequest = useRef(0);

  const currentTechnician = useMemo(
    () =>
      catalogs?.tecnicos?.find(
        technician =>
          String(technician.id) === String(user?.IDTecnicoOS || "")
      ),
    [catalogs, user]
  );

  const statuses = useMemo(
    () =>
      (catalogs?.status || []).filter(
        status => String(status.grupo_id) === String(groupId)
      ),
    [catalogs, groupId]
  );

  const boardCacheKey = useMemo(
    () => [
      "os-kanban",
      localStorage.getItem("companyId") || "default",
      String(groupId || ""),
      String(priorityId || ""),
      periodDays,
      scope,
      String(user?.IDTecnicoOS || ""),
      String(currentTechnician?.grupo_id || "")
    ],
    [
      currentTechnician,
      groupId,
      priorityId,
      periodDays,
      scope,
      user
    ]
  );

  useEffect(() => {
    if (groupId || !catalogs?.grupos?.length) return;
    setGroupId(
      currentTechnician?.grupo_id || catalogs.grupos[0]?.id || ""
    );
  }, [catalogs, currentTechnician, groupId]);

  useEffect(() => {
    if (catalogsError) toastError(catalogsError);
  }, [catalogsError]);

  const loadBoard = useCallback(async () => {
    if (!groupId) return;

    const requestId = ++boardRequest.current;
    setLoading(true);
    try {
      const board = await queryClient.fetchQuery(
        boardCacheKey,
        async () => {
          const params = {
            grupo_id: groupId,
            limite_por_status: 20,
            periodo_dias: periodDays
          };

          if (priorityId) params.prioridade_id = priorityId;
          if (scope === "mine" && user?.IDTecnicoOS) {
            params.tecnico_id = user.IDTecnicoOS;
          }
          if (scope === "group" && currentTechnician?.grupo_id) {
            params.tecnico_grupo_id = currentTechnician.grupo_id;
          }

          const { data } = await api.get("/os/ordens-servico-kanban", {
            params
          });
          return data;
        },
        { staleTime: TWO_MINUTES }
      );
      if (requestId === boardRequest.current) {
        setOrders(board.data || []);
        setCounts(board.contagens || {});
      }
    } catch (err) {
      if (requestId === boardRequest.current) toastError(err);
    } finally {
      if (requestId === boardRequest.current) setLoading(false);
    }
  }, [
    currentTechnician,
    boardCacheKey,
    groupId,
    priorityId,
    periodDays,
    queryClient,
    scope,
    user
  ]);

  useEffect(() => {
    loadBoard();
    return () => { boardRequest.current += 1; };
  }, [loadBoard]);

  const visibleOrders = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    if (!term) return orders;

    return orders.filter(order =>
      [
        order.id,
        order.descricao_curta,
        order.cliente?.razao,
        order.cliente?.fantasia,
        order.objeto?.nome,
        order.tecnico?.nome
      ].some(value =>
        String(value || "")
          .toLocaleLowerCase("pt-BR")
          .includes(term)
      )
    );
  }, [orders, search]);

  const moveOrder = useCallback(
    async (order, targetStatus) => {
      if (
        !order ||
        !targetStatus ||
        String(order.status_id) === String(targetStatus.id) ||
        movingId
      ) {
        return;
      }

      const previousOrders = orders;
      const previousCounts = counts;
      const sourceStatusId = String(order.status_id);
      const targetStatusId = String(targetStatus.id);
      const optimisticOrders = orders.map(item =>
        item.id === order.id
          ? {
              ...item,
              status_id: targetStatus.id,
              status: { id: targetStatus.id, nome: targetStatus.nome }
            }
          : item
      );
      const optimisticCounts = {
        ...counts,
        [sourceStatusId]: Math.max(
          0,
          Number(counts[sourceStatusId] || 0) - 1
        ),
        [targetStatusId]: Number(counts[targetStatusId] || 0) + 1
      };

      setMovingId(order.id);
      setOrders(optimisticOrders);
      setCounts(optimisticCounts);
      queryClient.setQueryData(boardCacheKey, current => ({
        ...(current || {}),
        data: optimisticOrders,
        contagens: optimisticCounts
      }));

      try {
        const { data } = await api.put(
          `/os/ordens-servico/${order.id}`,
          updatePayload(order, targetStatus.id)
        );
        const updated = data.data || data;
        const updatedOrders = optimisticOrders.map(item =>
          item.id === order.id ? { ...item, ...updated } : item
        );
        setOrders(updatedOrders);
        queryClient.setQueryData(boardCacheKey, current => ({
          ...(current || {}),
          data: updatedOrders,
          contagens: optimisticCounts
        }));
        toast.success(
          isFinalStatus(targetStatus)
            ? `OS #${order.id} finalizada.`
            : `OS #${order.id} movida para ${targetStatus.nome}.`
        );
      } catch (err) {
        setOrders(previousOrders);
        setCounts(previousCounts);
        queryClient.setQueryData(boardCacheKey, current => ({
          ...(current || {}),
          data: previousOrders,
          contagens: previousCounts
        }));
        toastError(err);
      } finally {
        setMovingId(null);
        setDraggingId(null);
      }
    },
    [boardCacheKey, counts, movingId, orders, queryClient]
  );

  const openMenu = (event, order) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuOrder(order);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuOrder(null);
  };

  const menuAction = action => {
    const selectedOrder = menuOrder;
    closeMenu();
    action(selectedOrder);
  };

  const interactionSaved = async () => {
    setInteractionOrder(null);
    await queryClient.invalidateQueries(boardCacheKey);
    await loadBoard();
  };

  const refreshBoard = async () => {
    await queryClient.invalidateQueries(boardCacheKey);
    await loadBoard();
  };

  const statusColor = (status, index) =>
    isFinalStatus(status)
      ? theme.palette.success?.main || "#00a859"
      : statusPalette[index % statusPalette.length];

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>Kanban de Ordens de Serviço</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Atualizar">
            <IconButton onClick={refreshBoard} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <OsInteractionDialog
        open={Boolean(interactionOrder)}
        onClose={() => setInteractionOrder(null)}
        onSaved={interactionSaved}
        order={interactionOrder}
        objectOptions={catalogs?.objetosConclusao || []}
        canFinalize={!interactionOrder?.data_fechada}
      />

      <Paper className={classes.filters} variant="outlined">
        <FormControl
          className={classes.filterField}
          variant="outlined"
          size="small"
        >
          <InputLabel>Grupo</InputLabel>
          <Select
            value={groupId}
            onChange={event => setGroupId(event.target.value)}
            label="Grupo"
          >
            {(catalogs?.grupos || []).map(group => (
              <MenuItem key={group.id} value={group.id}>
                {group.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          className={classes.filterField}
          variant="outlined"
          size="small"
        >
          <InputLabel>Prioridade</InputLabel>
          <Select
            value={priorityId}
            onChange={event => setPriorityId(event.target.value)}
            label="Prioridade"
          >
            <MenuItem value="">Todas</MenuItem>
            {(catalogs?.prioridades || []).map(priority => (
              <MenuItem key={priority.id} value={priority.id}>
                {priority.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.filterField} variant="outlined" size="small">
          <InputLabel id="kanban-period-label">Período</InputLabel>
          <Select
            labelId="kanban-period-label"
            value={periodDays}
            onChange={event => setPeriodDays(event.target.value)}
            label="Período"
          >
            {[30, 60, 90, 120].map(days => (
              <MenuItem key={days} value={days}>Últimos {days} dias</MenuItem>
            ))}
          </Select>
        </FormControl>

        <RadioGroup
          className={classes.scope}
          row
          value={scope}
          onChange={event => setScope(event.target.value)}
          aria-label="Responsabilidade"
        >
          <FormControlLabel
            value="mine"
            control={<Radio color="primary" size="small" />}
            label="Minhas"
            disabled={!user?.IDTecnicoOS}
          />
          <FormControlLabel
            value="group"
            control={<Radio color="primary" size="small" />}
            label="Meu grupo"
            disabled={!currentTechnician?.grupo_id}
          />
          <FormControlLabel
            value="all"
            control={<Radio color="primary" size="small" />}
            label="Todas"
          />
        </RadioGroup>

        <TextField
          className={classes.searchField}
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Pesquisar nas OS carregadas"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <Paper className={classes.boardPaper} variant="outlined">
        {catalogsLoading || (loading && !orders.length) ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : !statuses.length ? (
          <Box className={classes.loading}>
            <Typography color="textSecondary">
              O grupo selecionado não possui status ativos.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.board}>
            {statuses.map((status, index) => {
              const laneOrders = visibleOrders.filter(
                order => String(order.status_id) === String(status.id)
              );
              const total = Number(counts[String(status.id)] || 0);
              const color = statusColor(status, index);

              return (
                <Paper
                  key={status.id}
                  className={classes.column}
                  variant="outlined"
                  onDragOver={event => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={event => {
                    event.preventDefault();
                    const orderId = event.dataTransfer.getData("text/os-id");
                    const order = orders.find(
                      item => String(item.id) === String(orderId)
                    );
                    moveOrder(order, status);
                  }}
                >
                  <Box
                    className={classes.columnHeader}
                    style={{ backgroundColor: color }}
                  >
                    <Typography variant="subtitle2">
                      {status.nome}
                    </Typography>
                    <Chip
                      className={classes.columnCount}
                      label={
                        search.trim() && laneOrders.length !== total
                          ? `${laneOrders.length}/${total}`
                          : total
                      }
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Box className={classes.cards}>
                    {!laneOrders.length ? (
                      <Box className={classes.empty}>
                        <Typography variant="body2">
                          {search.trim()
                            ? "Nenhuma OS encontrada"
                            : "Arraste uma OS para cá"}
                        </Typography>
                      </Box>
                    ) : (
                      laneOrders.map(order => (
                        <Paper
                          key={order.id}
                          className={`${classes.card} ${
                            draggingId === order.id ? classes.dragging : ""
                          }`}
                          style={{ borderLeftColor: color }}
                          variant="outlined"
                          draggable={movingId !== order.id}
                          onDragStart={event => {
                            setDraggingId(order.id);
                            event.dataTransfer.effectAllowed = "move";
                            event.dataTransfer.setData(
                              "text/os-id",
                              String(order.id)
                            );
                          }}
                          onDragEnd={() => setDraggingId(null)}
                          onClick={() =>
                            history.push(`/ordens-servico/${order.id}`)
                          }
                        >
                          <Box className={classes.cardHeader}>
                            <Typography className={classes.cardId}>
                              OS #{order.id}
                            </Typography>
                            <Chip
                              className={classes.priority}
                              label={order.prioridade?.nome || "Sem prioridade"}
                              size="small"
                              variant="outlined"
                            />
                            <IconButton
                              size="small"
                              aria-label={`Ações da OS ${order.id}`}
                              onClick={event => openMenu(event, order)}
                            >
                              {movingId === order.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <MoreVertIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Box>

                          <Typography className={classes.client}>
                            {order.cliente?.razao ||
                              order.cliente?.fantasia ||
                              "Cliente não informado"}
                          </Typography>
                          <Typography
                            className={classes.description}
                            variant="body2"
                          >
                            {order.descricao_curta || "Sem descrição curta"}
                          </Typography>
                          <Typography
                            className={classes.metadata}
                            variant="caption"
                            display="block"
                          >
                            {order.objeto?.nome || "Sem objeto"} ·{" "}
                            {order.tecnico?.nome || "Sem técnico"}
                          </Typography>

                          <Box className={classes.cardFooter}>
                            <Typography
                              className={classes.updatedAt}
                              variant="caption"
                            >
                              Alterada {formatDate(order.data_alterada)}
                            </Typography>
                            <Tooltip title="Interagir">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={event => {
                                  event.stopPropagation();
                                  setInteractionOrder(order);
                                }}
                              >
                                <AddCommentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Abrir detalhes">
                              <IconButton
                                size="small"
                                onClick={event => {
                                  event.stopPropagation();
                                  history.push(
                                    `/ordens-servico/${order.id}`
                                  );
                                }}
                              >
                                <AssignmentOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        keepMounted
      >
        <MenuItem
          onClick={() =>
            menuAction(order =>
              history.push(`/ordens-servico/${order.id}`)
            )
          }
        >
          <ListItemIcon className={classes.menuIcon}>
            <AssignmentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Abrir detalhes" />
        </MenuItem>
        <MenuItem
          onClick={() =>
            menuAction(order => setInteractionOrder(order))
          }
        >
          <ListItemIcon className={classes.menuIcon}>
            <AddCommentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Interagir" />
        </MenuItem>
        <Divider />
        {statuses
          .filter(
            status =>
              String(status.id) !== String(menuOrder?.status_id || "")
          )
          .map(status => (
            <MenuItem
              key={status.id}
              onClick={() =>
                menuAction(order => moveOrder(order, status))
              }
            >
              <ListItemIcon className={classes.menuIcon}>
                {isFinalStatus(status) ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  <SwapHorizIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary={`Mover para ${status.nome}`} />
            </MenuItem>
          ))}
      </Menu>
    </MainContainer>
  );
};

export default Kanban;
